import { useContext, useState } from 'react';
import { api, type CardView, type Feedback, type FeedbackResult, type Interest, type QualityReason } from '../api';
import { EditionContext } from './detail-link';
import { Notice } from './ui';

/**
 * Two kinds of feedback (ADR-028), kept apart on screen as in the data:
 *
 *   interest  "Mehr davon / Weniger davon" on the card. The only feedback that
 *             moves ranking, and it moves topics and subjects, never a publisher.
 *   quality   "Stimmt / Stimmt nicht" in the details: feedback on Hugarheim's
 *             own processing. It opens a case for review and never moves ranking.
 *
 * Both bind to the exact version on screen.
 */

export const REASON_LABELS: Record<QualityReason, string> = {
  summary_wrong: 'Zusammenfassung falsch',
  stories_mixed: 'Zwei Meldungen vermischt',
  missing_important: 'Wichtiges fehlt',
  nothing_new: 'Nichts Neues',
};

const REASONS = Object.keys(REASON_LABELS) as QualityReason[];

/** What an interest rating applies to, in words. */
export function effectText(r: FeedbackResult | null): string {
  if (!r || !r.feedback.interest) return '';
  if (r.interest_effect.length === 0) {
    return 'Gespeichert. Diese Meldung hat noch kein Thema oder Subjekt, an dem Interesse ansetzen kann; das Ranking ändert sich dadurch nicht.';
  }
  const names = r.interest_effect.map((e) => (e.kind === 'topic' ? `Thema ${e.label}` : e.label)).join(', ');
  return `Gespeichert. Wirkt künftig auf: ${names}. Nicht auf die Quelle.`;
}

export function InterestButtons({ view, briefingId }: { view: CardView; briefingId?: string | null }) {
  const fromContext = useContext(EditionContext);
  const edition = briefingId ?? fromContext;
  const [interest, setInterest] = useState<Interest | null>(view.feedback?.interest ?? null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<FeedbackResult | null>(null);
  const [error, setError] = useState('');
  if (view.card.section !== 'news') return null;

  const choose = async (next: Interest) => {
    const value = interest === next ? null : next;
    setBusy(true);
    setError('');
    try {
      const r = await api.saveFeedback({ card_version_id: view.version.id, briefing_id: edition ?? null, interest: value });
      setInterest(r.feedback.interest ?? null);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="interest" role="group" aria-label={`Interesse an Meldungen wie: ${view.version.title}`}>
      <button type="button" className="text-button" aria-pressed={interest === 'more'} disabled={busy} onClick={() => choose('more')}>
        Mehr davon
      </button>
      <button type="button" className="text-button" aria-pressed={interest === 'less'} disabled={busy} onClick={() => choose('less')}>
        Weniger davon
      </button>
      <span className="visually-hidden" role="status">
        {effectText(result)}
      </span>
      {result && result.feedback.interest && <span className="interest-effect muted small">{effectText(result)}</span>}
      {error && (
        <span className="inline-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

/**
 * "Stimmt / Stimmt nicht" for the version shown, with the reason chips and an
 * optional note. A "Stimmt nicht" is a case for the quality review.
 */
export function QualityForm({
  versionId,
  versionNumber,
  existing,
  briefingId,
}: {
  versionId: string;
  versionNumber: number;
  existing: Feedback | null;
  briefingId?: string | null;
}) {
  const initial = {
    quality: existing?.quality ?? null,
    reasons: existing?.quality_reasons ?? [],
    note: existing?.note ?? '',
  };
  const [quality, setQuality] = useState<'correct' | 'incorrect' | null>(initial.quality);
  const [reasons, setReasons] = useState<QualityReason[]>(initial.reasons);
  const [note, setNote] = useState(initial.note);
  const [base, setBase] = useState(initial);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState('');
  const [caseId, setCaseId] = useState<string | null>(null);
  const dirty = quality !== base.quality || note !== base.note || reasons.join() !== base.reasons.join();

  const toggleReason = (r: QualityReason) => setReasons((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const save = async () => {
    setState('saving');
    setError('');
    try {
      const res = await api.saveFeedback({
        card_version_id: versionId,
        briefing_id: briefingId ?? null,
        quality,
        quality_reasons: quality === 'incorrect' ? reasons : [],
        note: note.trim() || null,
      });
      setBase({ quality, reasons: quality === 'incorrect' ? reasons : [], note });
      setCaseId(res.quality_case_id ?? null);
      setState('saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setState('error');
    }
  };

  return (
    <section className="feedback" aria-labelledby="fb-h">
      <h3 id="fb-h">Hat Hugarheim das richtig verarbeitet?</h3>
      <p className="muted small">
        Gilt für Version {versionNumber}. Es geht um Hugarheims Verarbeitung (Zusammenfassung, Zuordnung, Vollständigkeit), nicht um den Artikel. Diese
        Rückmeldung verändert das Ranking nicht; sie wird geprüft.
      </p>
      <div className="button-row" role="group" aria-label="Verarbeitung">
        <button type="button" className="small-button" aria-pressed={quality === 'correct'} onClick={() => setQuality(quality === 'correct' ? null : 'correct')}>
          Stimmt
        </button>
        <button
          type="button"
          className="small-button"
          aria-pressed={quality === 'incorrect'}
          onClick={() => setQuality(quality === 'incorrect' ? null : 'incorrect')}
        >
          Stimmt nicht
        </button>
      </div>
      {quality === 'incorrect' && (
        <fieldset className="reason-chips">
          <legend className="field-label">Was stimmt nicht? (mehrere möglich)</legend>
          {REASONS.map((r) => (
            <label key={r} className={`chip-toggle${reasons.includes(r) ? ' is-on' : ''}`}>
              <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggleReason(r)} />
              {REASON_LABELS[r]}
            </label>
          ))}
        </fieldset>
      )}
      <label className="field-label" htmlFor={`fb-note-${versionId}`}>
        Erklärung (optional, bleibt privat und kommt nie in Testfälle)
      </label>
      <textarea id={`fb-note-${versionId}`} value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} />
      <div className="button-row">
        <button type="button" className="button primary" disabled={state === 'saving' || (!dirty && state !== 'error')} onClick={save}>
          {state === 'saving' ? 'Speichert…' : 'Rückmeldung speichern'}
        </button>
        <span role="status" className="muted small">
          {state === 'saved' && !dirty
            ? caseId
              ? `Gespeichert für Version ${versionNumber}; als Fall zur Prüfung vorgemerkt.`
              : `Gespeichert für Version ${versionNumber}.`
            : existing?.quality && !dirty
              ? 'Bereits beurteilt.'
              : ''}
        </span>
      </div>
      {state === 'error' && <Notice kind="danger">{error}</Notice>}
    </section>
  );
}
